import React, { useEffect, useState } from "react";
import DashboardShell from "../../components/DashboardShell";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import {
  UserPlus, Copy, Check, Trash2, RefreshCw, Shield, ShieldCheck,
  Users, Clock, XCircle, Loader2, Mail,
} from "lucide-react";

const roleMeta = {
  owner:   { icon: ShieldCheck, chip: "chip-gold",  label: "Owner"   },
  admin:   { icon: Shield,      chip: "chip-orange", label: "Admin"   },
  member:  { icon: Users,       chip: "chip-navy",  label: "Member"  },
  viewer:  { icon: Users,       chip: "chip-navy",  label: "Viewer"  },
};

const statusMeta = {
  pending:  { chip: "chip-gold",  label: "Pending"  },
  accepted: { chip: "chip-navy",  label: "Accepted" },
  revoked:  { chip: "chip-red",   label: "Revoked"  },
  expired:  { chip: "chip-red",   label: "Expired"  },
};

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState({ email: "", role: "member" });
  const [copiedId, setCopiedId] = useState(null);

  const canManage = user?.role === "owner" || user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const [m, i] = await Promise.all([
        api.get("/v1/team/members"),
        api.get("/v1/team/invitations"),
      ]);
      setMembers(m.data);
      setInvites(i.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submitInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const { data } = await api.post("/v1/team/invite", {
        email: form.email || null,
        role: form.role,
      });
      toast.success("Invitation created — copy the link and share it");
      setForm({ email: "", role: "member" });
      setInvites([data, ...invites]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to invite");
    } finally {
      setInviting(false);
    }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm("Revoke this invitation? The link will stop working.")) return;
    try {
      await api.post(`/v1/team/invitations/${id}/revoke`);
      setInvites(invites.map(v => v.id === id ? { ...v, status: "revoked" } : v));
      toast.success("Invitation revoked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke");
    }
  };

  const removeMember = async (uid, name) => {
    if (!window.confirm(`Remove ${name} from the workspace?`)) return;
    try {
      await api.delete(`/v1/team/members/${uid}`);
      setMembers(members.filter(m => m.id !== uid));
      toast.success(`${name} removed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove");
    }
  };

  const copyLink = async (id, link) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1600);
      toast.success("Invite link copied");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const pending = invites.filter(i => i.status === "pending");
  const historic = invites.filter(i => i.status !== "pending");

  return (
    <DashboardShell
      title="Team"
      subtitle="Invite teammates, assign roles, and manage workspace access."
      actions={
        <button
          data-testid="team-refresh"
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-white/5 border border-white/10 text-cream hover:bg-white/10"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      }
    >
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Invite form */}
        <div className="lg:col-span-1">
          <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-6">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-[#FF6B35]/15 flex items-center justify-center">
                <UserPlus size={18} className="text-[#FF6B35]" />
              </div>
              <div>
                <div className="font-display font-bold text-cream">Invite a teammate</div>
                <div className="text-[11px] text-cream/50">Generates a shareable link · 7-day expiry</div>
              </div>
            </div>

            {!canManage ? (
              <div className="mt-5 text-sm text-cream/60 bg-white/5 border border-white/10 rounded-lg p-3">
                Only workspace <span className="font-semibold text-cream">owners</span> and <span className="font-semibold text-cream">admins</span> can invite members.
              </div>
            ) : (
              <form onSubmit={submitInvite} data-testid="team-invite-form" className="space-y-3 mt-5">
                <div>
                  <label className="eyebrow text-cream/60 text-xs">Email (optional)</label>
                  <div className="relative mt-1">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/40" />
                    <input
                      data-testid="team-invite-email"
                      type="email"
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      placeholder="engineer@company.com"
                      className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-[#0A0E1A] border border-white/10 text-cream outline-none focus:ring-2 focus:ring-[#FF6B35]/40 text-sm"
                    />
                  </div>
                  <div className="text-[11px] text-cream/40 mt-1">Leave blank for an open link.</div>
                </div>
                <div>
                  <label className="eyebrow text-cream/60 text-xs">Role</label>
                  <select
                    data-testid="team-invite-role"
                    value={form.role}
                    onChange={e => setForm({ ...form, role: e.target.value })}
                    className="w-full mt-1 px-3 py-2.5 rounded-lg bg-[#0A0E1A] border border-white/10 text-cream outline-none focus:ring-2 focus:ring-[#FF6B35]/40 text-sm"
                  >
                    <option value="admin">Admin · manage policies + team</option>
                    <option value="member">Member · read + write policies</option>
                    <option value="viewer">Viewer · read-only</option>
                  </select>
                </div>
                <button
                  data-testid="team-invite-submit"
                  disabled={inviting}
                  className="btn-primary w-full justify-center text-sm"
                >
                  {inviting ? <><Loader2 size={14} className="animate-spin" /> Creating…</> : <><UserPlus size={14} /> Create invitation</>}
                </button>
              </form>
            )}

            <div className="mt-6 pt-5 border-t border-white/10">
              <div className="eyebrow text-cream/50 text-[10px] mb-3">Roles at a glance</div>
              <ul className="space-y-2 text-xs text-cream/70">
                <li className="flex gap-2"><ShieldCheck size={13} className="text-[#FFD98A] mt-0.5" /> <b className="text-cream">Owner</b> — full control, cannot be removed.</li>
                <li className="flex gap-2"><Shield size={13} className="text-[#FF6B35] mt-0.5" /> <b className="text-cream">Admin</b> — invite, manage members, edit policies.</li>
                <li className="flex gap-2"><Users size={13} className="text-cream/70 mt-0.5" /> <b className="text-cream">Member</b> — read + edit policies, no team access.</li>
                <li className="flex gap-2"><Users size={13} className="text-cream/50 mt-0.5" /> <b className="text-cream">Viewer</b> — read-only on decisions and policies.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Members + Invites */}
        <div className="lg:col-span-2 space-y-6">
          <MembersCard members={members} loading={loading} onRemove={removeMember} canManage={canManage} selfId={user?.id} />
          <PendingInvitesCard invites={pending} onCopy={copyLink} copiedId={copiedId} onRevoke={revokeInvite} canManage={canManage} loading={loading} />
          {historic.length > 0 && (
            <HistoricInvitesCard invites={historic} />
          )}
        </div>
      </div>
    </DashboardShell>
  );
}

function MembersCard({ members, loading, onRemove, canManage, selfId }) {
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-[#FF6B35]" />
          <div className="font-display font-bold text-cream">Members</div>
          <span className="chip chip-navy" style={{background:'rgba(247,244,239,0.08)', color:'#F7F4EF'}}>{members.length}</span>
        </div>
      </div>

      {loading ? (
        <div className="text-cream/60 text-sm py-8 text-center"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</div>
      ) : (
        <div className="divide-y divide-white/5">
          {members.map(m => {
            const meta = roleMeta[m.role] || roleMeta.member;
            const RoleIcon = meta.icon;
            return (
              <div key={m.id} data-testid={`team-member-${m.id}`} className="py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#FF6B35] flex items-center justify-center font-bold text-white shrink-0">
                  {m.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-cream flex items-center gap-2">
                    <span className="font-medium">{m.name}</span>
                    {m.id === selfId && <span className="text-[10px] text-cream/50 uppercase tracking-widest">You</span>}
                  </div>
                  <div className="text-[12px] text-cream/50 truncate">{m.email}</div>
                </div>
                <div className={`chip ${meta.chip}`}><RoleIcon size={12} /> {meta.label}</div>
                {canManage && m.role !== "owner" && m.id !== selfId && (
                  <button
                    data-testid={`team-member-remove-${m.id}`}
                    onClick={() => onRemove(m.id, m.name)}
                    className="p-1.5 rounded hover:bg-red-500/10 text-red-400"
                    title="Remove member"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PendingInvitesCard({ invites, onCopy, copiedId, onRevoke, canManage, loading }) {
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock size={18} className="text-[#FFD98A]" />
          <div className="font-display font-bold text-cream">Pending invitations</div>
          <span className="chip chip-gold">{invites.length}</span>
        </div>
      </div>

      {loading ? null : invites.length === 0 ? (
        <div className="text-cream/50 text-sm text-center py-8" data-testid="team-invites-empty">
          No pending invitations. Create one from the panel on the left.
        </div>
      ) : (
        <div className="space-y-2">
          {invites.map(inv => {
            const meta = roleMeta[inv.role] || roleMeta.member;
            const RoleIcon = meta.icon;
            const expires = new Date(inv.expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric" });
            return (
              <div key={inv.id} data-testid={`team-invite-${inv.id}`} className="rounded-xl bg-[#0A0E1A]/60 border border-white/10 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                    <Mail size={14} className="text-cream/60" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-cream truncate">
                      {inv.email || <span className="text-cream/50 italic">Open link (no email lock)</span>}
                    </div>
                    <div className="text-[11px] text-cream/50 flex gap-3">
                      <span>Invited by {inv.invited_by_name}</span>
                      <span>Expires {expires}</span>
                    </div>
                  </div>
                  <div className={`chip ${meta.chip}`}><RoleIcon size={12} /> {meta.label}</div>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <input
                    data-testid={`team-invite-link-${inv.id}`}
                    readOnly
                    value={inv.invite_link}
                    onFocus={e => e.target.select()}
                    className="flex-1 px-3 py-2 rounded-lg bg-[#0A0E1A] border border-white/10 text-cream/80 text-xs font-mono truncate"
                  />
                  <button
                    data-testid={`team-invite-copy-${inv.id}`}
                    onClick={() => onCopy(inv.id, inv.invite_link)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#FF6B35] text-white text-xs font-semibold hover:bg-[#D14F1F]"
                  >
                    {copiedId === inv.id ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                  </button>
                  {canManage && (
                    <button
                      data-testid={`team-invite-revoke-${inv.id}`}
                      onClick={() => onRevoke(inv.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-red-400 text-xs hover:bg-red-500/10"
                    >
                      <XCircle size={13} /> Revoke
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function HistoricInvitesCard({ invites }) {
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Clock size={18} className="text-cream/50" />
        <div className="font-display font-bold text-cream">History</div>
      </div>
      <div className="divide-y divide-white/5">
        {invites.map(inv => {
          const s = statusMeta[inv.status] || statusMeta.pending;
          return (
            <div key={inv.id} className="py-2 flex items-center gap-3 text-sm">
              <div className="min-w-0 flex-1 truncate text-cream/80">
                {inv.email || <span className="text-cream/40 italic">open link</span>}
                <span className="text-cream/40 ml-2">· {inv.role}</span>
              </div>
              <span className={`chip ${s.chip}`}>{s.label}</span>
              <span className="text-[11px] text-cream/40">{new Date(inv.created_at).toLocaleDateString()}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
